import { ErrorMessage, Field, Form, Formik } from 'formik';
import { useAtom } from 'jotai';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { profileAtom } from '../../../jotai-atoms/profile.atom';
import { AccountInformationWithOptionsProps } from '../../../models/Profile';
import { ProfileService } from '../../../services/profile';
import { InputFeedback } from '../../../stories/InputFeedback';
import { Modal } from '../../../stories/Modal';
import { Panel } from '../../../stories/Panel';
import SelectInput, { SelectInputOption } from '../../../stories/SelectInput';
import { TextInput } from '../../../stories/TextInput';
import {
  getLanguageName,
  getRecommendedTimezoneOption,
  getTimezoneOptionFromCtz,
} from '../../../utils';
import { useUpdateAccountInformation } from '../../../hooks/use-update-account-information';

interface AccountInformationModalProps {
  show: boolean;
  onClose: () => void;
}

const AccountInformationModal: FC<AccountInformationModalProps> = (props) => {
  const __ = useTranslation().t;
  const [errorStateActive, setErrorStateActive] = useState<boolean>(false);
  const { mutateAsync, isLoading } = useUpdateAccountInformation();
  const [profile, setProfile] = useAtom(profileAtom);

  const timezoneOptionFromCtz = getTimezoneOptionFromCtz(profile?.accountInformation?.timezone);
  const currentTimezoneLabel =
    (profile?.timezoneOptions &&
      profile?.accountInformation?.timezone &&
      profile?.timezoneOptions[profile?.accountInformation?.timezone]) ||
    timezoneOptionFromCtz?.label;

  const initialValues = {
    ...profile?.accountInformation,
    timezoneOption: {
      label: currentTimezoneLabel || '',
      value: profile?.accountInformation?.timezone || '',
    },
    languageOption: {
      label: useTranslation().t(getLanguageName(profile?.accountInformation?.language)) || '',
      value: profile?.accountInformation?.language || '',
    },
    subscriptionTypeOption: (profile?.accountInformation?.subscriptionType || [])?.join(', '),
  };

  const recommendedOption = getRecommendedTimezoneOption(profile?.timezoneOptions);

  const schema = yup.object().shape({
    timezoneOption: yup.object().nullable().required(__('fieldRequired')),
    languageOption: yup.object().nullable().required(__('fieldRequired')),
  });

  const modalProps = {
    show: props.show,
    type: 'default',
    title: __('accountInfo'),
    submitButtonText: __('save'),
    cancelButtonText: __('cancel'),
  };

  const onSubmit = (values: AccountInformationWithOptionsProps) => {
    const accountInformationWithToken = {
      ...values,
      timezone: values.timezoneOption?.value as string,
      language: values.languageOption?.value as string,
      _token: profile?._token,
    };
    mutateAsync(accountInformationWithToken, {
      onSuccess: () => {
        setErrorStateActive(false);
        props.onClose();
        if (accountInformationWithToken.language !== profile?.accountInformation?.language) {
          window.location.reload();
        }
        const {
          timezoneOption,
          languageOption,
          subscriptionTypeOption,
          _token,
          ...accountInformation
        } = accountInformationWithToken;

        setProfile({
          ...profile,
          accountInformation,
        });
      },
      onError: () => {
        setErrorStateActive(true);
      }
    });
  };

  const onClose = (resetForm: () => void) => {
    resetForm();
    setErrorStateActive(false);
    props.onClose();
  };

  const timezoneOptions = Object.keys(profile?.timezoneOptions || {}).map((key: string) => {
    const label = profile?.timezoneOptions && profile?.timezoneOptions[key];
    return {
      label,
      value: key,
    } as SelectInputOption;
  });

  const languageOptions = Object.keys(profile?.languageOptions || {}).map((key: string) => {
    const value = profile?.languageOptions && profile?.languageOptions[key];
    return {
      label: value,
      value: key,
    } as SelectInputOption;
  });

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={schema}
      onSubmit={onSubmit}
    >
      {({ values, setFieldValue, submitForm, resetForm }) => (
        <Modal
          {...modalProps}
          submitButtonDisabled={isLoading}
          onClose={() => onClose(resetForm)}
          onSubmit={submitForm}
        >
          <Form className="oejr-profile-modal-form">
            <Panel
              variant="danger"
              hidden={!errorStateActive}
              onHide={() => setErrorStateActive(false)}
            >
              <p>{__('opsCheckTyposContactUs')}</p>
            </Panel>
            <Field
              type="text"
              name="username"
              label={__('username')}
              component={TextInput}
              disabled
            />
            <SelectInput
              label={__('timezone')}
              name="timezoneOption"
              options={timezoneOptions}
              value={values.timezoneOption}
              onChange={(option) => setFieldValue('timezoneOption', option)}
              searchable
              recommendedOption={recommendedOption}
            />
            <ErrorMessage
              name="timezoneOption"
              component={({ children }: any) => (
                <InputFeedback state="error" text={children} icon="exclamation-circle" />
              )}
            />
            <SelectInput
              label={__('language')}
              name="languageOption"
              options={languageOptions}
              value={values.languageOption}
              onChange={(option) => setFieldValue('languageOption', option)}
            />
            <ErrorMessage
              name="languageOption"
              component={({ children }: any) => (
                <InputFeedback state="error" text={children} icon="exclamation-circle" />
              )}
            />
            {!!initialValues.subscriptionTypeOption && (
              <Field
                type="text"
                name="subscriptionTypeOption"
                label={__('subscriptionType')}
                component={TextInput}
                disabled
              />
            )}
          </Form>
        </Modal>
      )}
    </Formik>
  );
};

export default AccountInformationModal;
